import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaFire } from 'react-icons/fa';
import { TbCalendarClock } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useAppointmentActions from '../../../hooks/APIHooks/appointments/useAppointmentActions';
import useRescheduleHistoryActions from '../../../hooks/APIHooks/appointments/useRescheduleHistoryActions';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import { rescheduleAppointmentSchema } from '../../../schemas';
import {
  AppointmentSlot,
  AvailableAssociate,
} from '../../../types/appointments';
import { SelectData } from '../../../types/common';
import {
  AppointmentHistory,
  RescheduleHistoryReqBody,
} from '../../../types/history';
import { Service } from '../../../types/services';
import { User } from '../../../types/users';
import { showFullName } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import {
  convertTimeSlotInto24HrsTime,
  convertTimeTo24Hrs,
} from '../../../utils/timeAndDate';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaDatePicker from '../Inputs/AcademiaDatePicker';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTimeSlot from './ModalTimeSlot';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const RescheduleAppointmentModal = ({
  handleModalClose,
  appointment,
}: {
  handleModalClose: () => void;
  appointment: AppointmentHistory;
}) => {
  // integration of react hooks here
  const [serviceList, setServiceList] = useState<Array<Service>>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedAssociate, setSelectedAssociate] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedService, setSelectedService] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [availableAssociates, setAvailableAssociates] = useState<
    Array<AvailableAssociate>
  >([]);

  const [maxMinIndex, setMaxMinIndex] = useState<Array<number>>([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<
    Array<{ index: number; slot: string }>
  >([]);
  const [availableSlots, setAvailableSlots] = useState<Array<AppointmentSlot>>(
    []
  );

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // integration of custom API hooks here
  const { createNewRescheduleHistory } = useRescheduleHistoryActions('');
  const { getServiceListHandler } = useServiceActions('serviceList');
  const {
    getAvailableAssociatesHandler,
    getAvailableSlotsHandler,
    getAppointmentsByAssociateIdHandler,
    getAppointmentsByClientIdHandler,
  } = useAppointmentActions('', currentUser?.id, currentUser?.id);

  //integration of tanstack query hooks here
  const queryClient = useQueryClient();

  console.log(
    'APPOINTMENT: ',
    appointment,
    appointment.date,
    new Date(appointment.date)
  );

  // setting the service list into a state here
  useEffect(() => {
    if (getServiceListHandler.data?.is_success) {
      setServiceList(getServiceListHandler.data.data);
    }
  }, [getServiceListHandler.data]);

  // finding existing service from service list here
  useEffect(() => {
    if (serviceList.length) {
      const existingService = serviceList.find(
        (service) => service.id === appointment.service_id
      );

      if (existingService?.id) {
        setSelectedService({
          id: existingService.id,
          name: existingService.name,
        });
      }
    }
  }, [serviceList, appointment.service_id]);

  // fetching available associates here
  useEffect(() => {
    if (selectedService.id !== -1 && selectedDate) {
      getAvailableAssociatesHandler
        .mutateAsync({
          body: {
            service_id: selectedService.id,
            selected_date: moment(selectedDate, 'DD-MM-YYYY').format(
              'YYYY-MM-DD'
            ),
          },
        })
        .then((res) => {
          if (res.data.is_success) {
            console.log(
              'AVAILABLE ASSOCIATES: ',
              res.data.available_associates
            );
            for (const key in res.data.available_associates) {
              if (
                key === moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD')
              ) {
                if (!res.data.available_associates[key].length) {
                  toast.warn('Sorry, No Available Associates!!');
                }

                setAvailableAssociates(res.data.available_associates[key]);
              }
            }
          }
        })
        .catch((error) => {
          setAvailableAssociates([]);
          toast.warn(fetchErrorMessage(error));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, selectedDate]);

  // fetching available slots here
  useEffect(() => {
    if (selectedAssociate.id !== -1) {
      getAvailableSlotsHandler
        .mutateAsync({
          body: {
            associate_id: selectedAssociate.id,
            service_id: selectedService.id,
            date: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
            current_time: new Date().toLocaleTimeString('en-GB', {
              hour12: false,
            }),
          },
        })
        .then((res) => {
          if (res.is_success) {
            setAvailableSlots(res.data);
          }
        })
        .catch((error) => toast.warn(fetchErrorMessage(error)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssociate]);

  // sorting selected appointments based on index here
  useEffect(() => {
    if (selectedTimeSlots.length) {
      const sortedTimeSlots = selectedTimeSlots.sort(
        (slotA, slotB) => slotA.index - slotB.index
      );

      setSelectedTimeSlots(sortedTimeSlots);

      // setting max & min index for disabling here
      setMaxMinIndex([
        sortedTimeSlots[0].index,
        sortedTimeSlots[sortedTimeSlots.length - 1].index,
      ]);
    }
  }, [selectedTimeSlots]);

  // handler function to reschedule appointment declared here
  const rescheduleAppointmentHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      // splitting the time slot here
      const { start_time, end_time } = convertTimeSlotInto24HrsTime(
        `${selectedTimeSlots[0].slot.split(' - ')[0]} - ${
          selectedTimeSlots[selectedTimeSlots.length - 1].slot.split(' - ')[1]
        }`
      );

      const body: RescheduleHistoryReqBody = {
        appointment_request: appointment.id,
        service: values.service.id,
        previous_date: moment(appointment.date, 'DD-MM-YYYY').format(
          'YYYY-MM-DD'
        ),
        previous_start_time: convertTimeTo24Hrs(appointment.start_time),
        previous_end_time: convertTimeTo24Hrs(appointment.end_time),
        reschedule_date: moment(values.appointment_date, 'DD-MM-YYYY').format(
          'YYYY-MM-DD'
        ),
        new_start_time: start_time,
        new_end_time: end_time,
        previous_associate: appointment.associate,
        new_associate: values.associate.id,
        reason_for_rescheduling: values.reschedule_reason,
        reschedule_status: 'pending',
        created_by: currentUser?.username,
      };

      console.log('RESCHEDULE REQ BODY: ', body);

      await createNewRescheduleHistory
        .mutateAsync({ body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Appointment Rescheduled!!');

            handleModalClose();

            if (currentUserType === 'user') {
              if ((currentUser as User)?.role_name === 'Admin') {
                queryClient.invalidateQueries({
                  queryKey: ['appointment-request-list'],
                });
              } else {
                getAppointmentsByAssociateIdHandler.refetch();
              }
            } else {
              queryClient.invalidateQueries({
                queryKey: ['current-client-details', currentUser?.id],
              });

              getAppointmentsByClientIdHandler.refetch();
            }
          } else {
            toast.error(res.message || 'Rescheduling Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to update array on select time slot declared here
  const onSelectTimeSlotHandler = (index: number, slotDuration: string) => {
    if (selectedTimeSlots.length) {
      if (selectedTimeSlots.filter((slot) => slot.index === index).length) {
        const prevTimeSlot = selectedTimeSlots.find(
          (slot) => slot.index === index - 1
        );
        const nextTimeSlot = selectedTimeSlots.find(
          (slot) => slot.index === index + 1
        );

        if (prevTimeSlot?.index && nextTimeSlot?.index) {
          toast.warn('Cannot Deselect This Slot!!');
        } else {
          setSelectedTimeSlots((state) =>
            state.filter((slot) => slot.index !== index)
          );
        }
      } else {
        setSelectedTimeSlots((state) => [
          ...state,
          { index, slot: slotDuration },
        ]);
      }
    } else {
      setSelectedTimeSlots((state) => [
        ...state,
        { index, slot: slotDuration },
      ]);
    }
  };

  // function to check if time slot selected or not declared here
  const isTimeSlotSelected = (index: number): boolean => {
    return !!selectedTimeSlots.filter((slot) => slot.index === index).length;
  };

  // rendering reschedule appointment modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='reschedule appointment' />

      {selectedService.id !== -1 ? (
        <Formik
          initialValues={{
            service: {
              id: appointment.service_id,
              name: appointment.service_name,
            },
            appointment_date: '',
            associate: SELECT_FIELD_DEFAULT_VALUE,
            time_slot: '',
            reschedule_reason: '',
          }}
          validationSchema={rescheduleAppointmentSchema}
          onSubmit={rescheduleAppointmentHandler}
        >
          {(props) => (
            <Form className='space-y-5'>
              <AcademiaFormSelect
                name='service'
                label='Select Service'
                options={
                  serviceList.length
                    ? serviceList.map((service) => ({
                        id: service.id,
                        name: service.name,
                      }))
                    : []
                }
                defaultValue={props.values.service}
                optionDescription={
                  serviceList.length
                    ? serviceList.map((service) =>
                        service.is_most_popular ? (
                          <FaFire
                            title='Popular Right Now'
                            className='text-base-orange'
                          />
                        ) : (
                          <></>
                        )
                      )
                    : undefined
                }
                setValueOnChange={(service) => setSelectedService(service)}
                showSearchInput
                isRequired
              />

              {appointment.date ? (
                <AcademiaDatePicker
                  name='appointment_date'
                  label='Select Date'
                  placeholder='DD-MM-YYYY'
                  minDate={new Date()}
                  defaultDate={
                    new Date(
                      moment(appointment.date, 'DD-MM-YYYY').format(
                        'YYYY-MM-DD'
                      )
                    ) < new Date()
                      ? new Date()
                      : new Date(
                          moment(appointment.date, 'DD-MM-YYYY').format(
                            'YYYY-MM-DD'
                          )
                        )
                  }
                  setDateOnChange={(date) => setSelectedDate(date)}
                  isRequired
                />
              ) : null}

              {availableAssociates.length ? (
                <>
                  <AcademiaFormSelect
                    name='associate'
                    label='Select Associate'
                    options={
                      availableAssociates.length
                        ? availableAssociates.map((associate) => ({
                            id: associate.id,
                            name: showFullName(
                              associate.first_name,
                              associate.middle_name,
                              associate.last_name
                            ),
                          }))
                        : []
                    }
                    defaultValue={props.values.associate}
                    setValueOnChange={(associate) =>
                      setSelectedAssociate(associate)
                    }
                    showSearchInput
                    isRequired
                  />

                  {availableSlots.length ? (
                    <>
                      <motion.div
                        initial={{ x: 0 }}
                        animate={
                          props.touched.time_slot && props.errors.time_slot
                            ? {
                                x: [0, -10, 0, 10, 0],
                                transition: {
                                  duration: 0.1,
                                  repeat: 2,
                                },
                              }
                            : { x: 0 }
                        }
                        className={`h-[210px] overflow-y-auto space-y-4 px-4 py-2 academia-scrollbar border-2 rounded-lg ${
                          props.touched.time_slot && props.errors.time_slot
                            ? 'border-base-red'
                            : 'border-base-cyan'
                        }`}
                      >
                        {availableSlots.length ? (
                          availableSlots.map((slot, index) => (
                            <ModalTimeSlot
                              key={index}
                              index={index}
                              id={`time-slot-${index}`}
                              name={`time-slot-${index}`}
                              slot={slot}
                              onSelect={onSelectTimeSlotHandler}
                              isSlotSelected={isTimeSlotSelected}
                              isDisabled={
                                selectedTimeSlots.length
                                  ? !(
                                      index >= maxMinIndex[0] - 1 &&
                                      index <=
                                        maxMinIndex[maxMinIndex.length - 1] + 1
                                    )
                                  : false
                              }
                            />
                          ))
                        ) : (
                          <p className='text-base-red font-medium text-center text-lg'>
                            No Slots Found!!
                          </p>
                        )}
                      </motion.div>

                      {/* <motion.span
                        initial={{ y: -20, opacity: 0, display: 'none' }}
                        animate={
                          props.touched.time_slot && props.errors.time_slot
                            ? { y: 0, opacity: 1, display: 'block' }
                            : {
                                y: -20,
                                opacity: 0,
                                transitionEnd: { display: 'none' },
                              }
                        }
                        className='block mt-1 text-base-red text-base'
                      >
                        {props.errors.time_slot}
                      </motion.span> */}
                    </>
                  ) : null}

                  <AcademiaTextAreaField
                    name='reschedule_reason'
                    label='Reschedule Reason'
                    placeholder='Enter reschedule reason here...'
                    isRequired
                  />
                </>
              ) : null}

              <AcademiaButton
                title={
                  props.values.associate.id === -1
                    ? 'Please, select an associate first!'
                    : undefined
                }
                type='submit'
                variant='premium'
                icon={<TbCalendarClock className='size-6' />}
                isLoading={props.isSubmitting}
                isDisabled={props.values.associate.id === -1}
                fullWidth
              >
                Reschedule
              </AcademiaButton>
            </Form>
          )}
        </Formik>
      ) : null}
    </ParentModal>
  );
};

export default RescheduleAppointmentModal;
